import type { DieSpellId, SlotSpellId } from './types';
import { BALANCE } from '../data/balance';
import { DIE_SPELL_BY_ID } from '../data/dieSpells';
import { SLOT_SPELL_BY_ID } from '../data/slotSpells';
import {
  fourFromThree,
  grimoireFactor,
  grimoireMultiplier,
  maxrollMultiplier,
  numbersWidened,
  straightGapAllowed,
} from './combos';

/**
 * Textes des sorts **avec les Grimoires appliqués** (v0.47.0, A94-11) :
 * conditions élargies (*Grimoire des nombres* niveau 3, *Face je gagne*
 * niveaux 3 et 5) et multiplicateurs renforcés. Lu par
 * `Game.spellDescription` ; sans Grimoire, les textes reprennent les chiffres
 * de `data/slotSpells.ts` et `data/dieSpells.ts`. Fonction pure.
 */
export interface SpellTextContext {
  /** Niveau du *Grimoire des nombres* (0 à 5). */
  grimoireNumbers: number;
  /** Niveau de *Face je gagne, Pile tu perds* (0 à 5). */
  grimoireFaces: number;
  /** Emplacements débloqués (mise à l'échelle de *Maxroll*). */
  slots: number;
}

/** Condition et effet d'un sort, en clair. */
export interface SpellText {
  condition: string;
  effect: string;
}

/** Nombre à la française, jusqu'à trois décimales utiles. */
function fr(n: number): string {
  const s = (Math.round(n * 1000) / 1000).toFixed(3);
  return s.replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
}

/** « renforcé : 1 + (m − 1) × 1,3 » quand un Grimoire agit sur un multiplicateur calculé. */
function dynamicBoost(level: number): string {
  if (!(level > 0)) return '';
  return ` ; bonus renforcé : 1 + (m − 1) × ${fr(grimoireFactor(level))}`;
}

function slotText(id: SlotSpellId, ctx: SpellTextContext): SpellText {
  const def = SLOT_SPELL_BY_ID[id];
  const level = Math.max(0, ctx.grimoireFaces);
  const gap = straightGapAllowed(level) ? ', un trou toléré' : '';
  const times = (m: number): string =>
    `Multiplie les dégâts du lancer par ${fr(grimoireMultiplier(m, level))}.`;
  switch (id) {
    case 'pair':
    case 'triple':
    case 'doublePair':
    case 'triplePair':
    case 'doubleTriple':
    case 'snakeEyes':
      return { condition: def.condition, effect: times(def.multiplier) };
    case 'four':
      return {
        condition: fourFromThree(level)
          ? 'Trois dés Maîtres (ou plus) affichent la même face'
          : def.condition,
        effect: times(def.multiplier),
      };
    case 'straight':
      return {
        condition: `${def.condition}${gap}`,
        effect: `Multiplie les dégâts du lancer par m, la plus haute face de la suite${dynamicBoost(level)}.`,
      };
    case 'grandStraight':
      return {
        condition: `${def.condition}${gap}`,
        effect: `Multiplie les dégâts du lancer par m, la plus haute face de la suite au carré${dynamicBoost(level)}.`,
      };
    case 'maxiStraight':
      return {
        condition: `${def.condition}${gap}`,
        effect: `Multiplie les dégâts du lancer par m, la plus haute face de la suite au cube${dynamicBoost(level)}.`,
      };
    case 'full':
      return {
        condition: def.condition,
        effect: `Multiplie les dégâts du lancer par m, le produit des faces du brelan et de la paire${dynamicBoost(level)}.`,
      };
    case 'tripleFour':
      return {
        condition: def.condition,
        effect: `Multiplie les dégâts du lancer par m, la face du brelan × la face du carré${dynamicBoost(level)}.`,
      };
    default:
      return { condition: def.condition, effect: def.description };
  }
}

function dieText(id: DieSpellId, ctx: SpellTextContext): SpellText {
  const def = DIE_SPELL_BY_ID[id];
  const level = Math.max(0, ctx.grimoireNumbers);
  const wide = numbersWidened(level);
  const factor = grimoireFactor(level);
  const times = (m: number): string => fr(grimoireMultiplier(m, level));
  switch (id) {
    // v0.49.0 (A98-2) : plus de secondes, aucun renfort du Grimoire.
    case 'fortyTwo':
      return { condition: def.condition, effect: def.description };
    // v0.49.0 — COMMENTS v0.48.1 §1.3.2, Grimoire niveau 3 (A98-8).
    case 'twentyDict':
      return {
        condition: wide
          ? `Au moins ${BALANCE.spellTwentyDictWideCount} faces Maîtres strictement supérieures à ${BALANCE.spellTwentyDictFace}`
          : def.condition,
        effect: def.description,
      };
    case 'nullMark':
      return {
        condition: wide
          ? 'Au plus une paire de faces Maîtres identiques (au moins deux dés)'
          : def.condition,
        effect: def.description,
      };
    case 'storm': {
      const reduction = Math.min(0.95, BALANCE.spellStormReduction * factor);
      return {
        condition: wide
          ? `${BALANCE.spellStormWideCount} faces Maîtres strictement supérieures à ${BALANCE.spellStormFace}`
          : def.condition,
        effect:
          `Une tempête fait rage pendant ${BALANCE.spellStormDuration} secondes : votre temps de retour et ` +
          `celui du Larbin sont réduits de ${fr(reduction * 100)} %. La relancer rafraîchit sa durée.`,
      };
    }
    case 'steamroller':
      return {
        condition: wide
          ? 'La plus haute face Maître du lancer est au moins égale à celle du lancer précédent du combat, moins 1'
          : def.condition,
        effect:
          'Multiplie les dégâts du lancer par m, le produit de sa plus petite et de sa plus haute ' +
          `face Maître${dynamicBoost(level)}.`,
      };
    case 'parity':
      return {
        condition: wide
          ? 'Toutes les faces sont paires, ou toutes impaires ; à partir de trois dés, une face de l’autre parité est tolérée'
          : def.condition,
        effect: `Multiplie les dégâts du lancer par ${times(def.multiplier)}.`,
      };
    case 'pi':
      return {
        condition: wide ? 'Les faces 3 et 1 sont présentes, avec une face paire quelconque' : def.condition,
        effect: `Multiplie les dégâts du lancer par ${times(def.multiplier)}.`,
      };
    case 'maxroll': {
      const slots = Math.max(2, ctx.slots);
      return {
        condition: wide
          ? 'Tous les dés Maîtres affichent leur face maximale ; à partir de trois dés, une face au maximum − 1 est tolérée'
          : def.condition,
        effect: `Multiplie les dégâts du lancer par ${times(maxrollMultiplier(slots))} (avec ${slots} emplacements).`,
      };
    }
    case 'goldenRatio':
      return {
        condition: def.condition,
        effect: `Multiplie les gains en or du monstre à sa mort par ${fr(def.goldMult * factor)}.`,
      };
    case 'sixes':
      return {
        condition: wide ? 'Deux dés Maîtres affichent un 6' : def.condition,
        effect:
          'Le sol s’embrase jusqu’à la fin du combat en cours : tous les lancers voient leurs ' +
          `dégâts multipliés par ${times(BALANCE.spellInfernoMultiplier)}. L’effet ne se cumule pas.`,
      };
    case 'primes': {
      const seq = BALANCE.spellPrimesSequence.map((m) => times(m));
      return {
        condition: wide
          ? 'Au moins un dé Maître affiche une face première (2, 3, 5, 7, 11 … 97)'
          : def.condition,
        effect:
          `Multiplie les dégâts des ${seq.length} prochains lancers par ${seq.slice(0, -1).join(', ')} ` +
          `puis ${seq[seq.length - 1]}. Le lancer déclencheur n’est pas concerné et un ` +
          'redéclenchement pendant la séquence est ignoré.',
      };
    }
    default:
      return { condition: def.condition, effect: def.description };
  }
}

/** Condition et effet d'un sort d'emplacement ou de dé, Grimoires appliqués. */
export function describeSpell(id: SlotSpellId | DieSpellId, ctx: SpellTextContext): SpellText {
  if (Object.prototype.hasOwnProperty.call(SLOT_SPELL_BY_ID, id)) {
    return slotText(id as SlotSpellId, ctx);
  }
  if (Object.prototype.hasOwnProperty.call(DIE_SPELL_BY_ID, id)) {
    return dieText(id as DieSpellId, ctx);
  }
  return { condition: '', effect: '' };
}
